"use client";
import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Collapse, IconButton, Stack } from "@mui/material";
import {
  SearchRounded as ViewAllIcon,
  CheckRounded as SelectedIcon,
  MoreVertRounded as OverflowIcon
} from "@mui/icons-material";
import AgentIcon from "@mui-symbols-material/w400/PsychologyFilledRounded";
import SessionIcon from "@mui-symbols-material/w400/ModeCommentRounded";

import { useStore, observer } from "../../../service/mobx";
import { NavListItem } from "../index";
import ToolTip from "../../Tooltip";
import Menu from "./Menu";
import TimeAgo from "../../TimeAgo";
import Skeleton from "../../Skeleton";

function Sessions() {
  const [threads, setThreads] = useState([]);
  const { agent, layout, user } = useStore();
  const router = useRouter();

  useEffect(() => {
    const threads = agent.threadsArray.length
      ? agent.threadsArray
      : user.profile.threads ?? [];

    setThreads(threads.slice(0, 2));
  }, [user.profile.threads, agent.threadsArray]);

  return (
    <Stack spacing={0.5}>
      <ToolTip placement="right" title={layout.navOpen ? "" : "Research AI"}>
        <NavListItem
          line1="Agent"
          href="/agent"
          onClick={() => agent.set.threadId()}
          leading={{ Icon: AgentIcon }}
          sx={{
            "&:hover button": {
              opacity: 1,
              display: "flex"
            }
          }}
          trailing={
            threads.length < 2
              ? undefined
              : {
                  Icon: () => (
                    <ToolTip placement="right" title="Search chat history">
                      <IconButton
                        onClick={event => {
                          router.push("/agent/history");
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        sx={{
                          transition:
                            "opacity 300ms var(--motion-easing-emphasized)",
                          opacity: 0,
                          "&:hover": {
                            bgcolor: "var(--primary-container)",
                            svg: {
                              color: "var(--primary-on-container)"
                            }
                          }
                        }}
                      >
                        <ViewAllIcon sx={{ width: 20, height: 20 }} />
                      </IconButton>
                    </ToolTip>
                  )
                }
          }
        />
      </ToolTip>
      <Collapse in={layout.navOpen}>
        <Stack spacing={0.5} ml={4}>
          {threads.map(thread => (
            <Item
              key={thread.id ?? thread}
              loading={agent.loaded === false || thread.id === undefined}
              thread={thread.id === undefined ? undefined : thread}
            />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

export default observer(Sessions);

const Item = observer(function Item({ loading, thread = {} }) {
  const { agent, menu } = useStore();
  const pathname = usePathname();
  const router = useRouter();
  const selected = loading === false && agent.threadId === thread.id;

  return (
    <ToolTip
      placement="right"
      title={
        loading || agent.threadId === thread.id
          ? ""
          : "Continue where you left off"
      }
    >
      <NavListItem
        disabled={agent.running || loading}
        selected={selected}
        line1Sx={{ textTransform: "unset", typography: "bodyMd" }}
        line1={
          loading ? (
            <Skeleton borderRadius="xs-round" height={32} />
          ) : (
            <TimeAgo
              date={
                thread.history.length === 0
                  ? thread.created
                  : thread.history[thread.history.length - 1].created
              }
            />
          )
        }
        leading={{
          Icon: selected
            ? SelectedIcon
            : () => <SessionIcon sx={{ width: 20, height: 20 }} />
        }}
        onClick={event => {
          if (selected) {
            return event.preventDefault();
          }

          if (pathname === "/agent") {
            agent.set.threadId(thread.id);
          } else {
            router.push("/agent");
            setTimeout(agent.set.threadId, 1e3, thread.id);
          }
        }}
        trailing={
          selected
            ? {
                Icon: () => (
                  <IconButton
                    onClick={event => {
                      event.preventDefault();

                      menu.configure({
                        anchor: event.currentTarget,
                        Component: Menu,
                        sx: {
                          menuList: {
                            py: 0
                          },
                          paper: {
                            width: 248,
                            maxHeight: "unset",
                            borderRadius: "var(--shape-sm)"
                          }
                        },
                        options: {
                          anchorOrigin: {
                            vertical: "center",
                            horizontal: "right"
                          },
                          transformOrigin: {
                            vertical: "center",
                            horizontal: "left"
                          }
                        }
                      });
                    }}
                  >
                    <OverflowIcon />
                  </IconButton>
                )
              }
            : undefined
        }
      />
    </ToolTip>
  );
});
